// NEW_FILE_JUSTIFIED: Import policy hook — manages file upload, AI extraction, and save flow for the ImportPolicyDialog
import { useState, useCallback } from 'react';
import { useMutation, useAction } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: hooks need convex api
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks need convex Id type
import type { IExtractedPolicyData, IPolicyFieldValue } from '@/@types/insurance';

// LOCAL_TYPE_JUSTIFIED: Internal hook state types — only used within this hook
type TImportStep = 'upload' | 'extracting' | 'review' | 'saving' | 'error';

interface IImportState {
  step: TImportStep;
  uploadedFileIds: Id<'_storage'>[];
  uploadedFileNames: string[];
  extractedData: IExtractedPolicyData | null;
  error: string | null;
  duplicateWarning: string | null;
}

const initialState: IImportState = {
  step: 'upload',
  uploadedFileIds: [],
  uploadedFileNames: [],
  extractedData: null,
  error: null,
  duplicateWarning: null,
};

export function useImportPolicy(orgId: Id<'organisations'> | null) {
  const [state, setState] = useState<IImportState>(initialState);

  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const extractPolicyData = useAction(api['insurance/importPolicy'].extractPolicyData);
  const createPolicy = useMutation(api['insurance/policies'].create);
  const updatePolicy = useMutation(api['insurance/policies'].update);
  const createDriver = useMutation(api['insurance/drivers'].create);
  const matchOrCreateCustomer = useMutation(api.customers.matchOrCreate);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      // Show skeleton immediately when files are selected
      setState(prev => ({
        ...prev,
        step: 'extracting',
        uploadedFileNames: files.map(f => f.name),
        error: null,
      }));

      const fileIds: Id<'_storage'>[] = [];
      const fileNames: string[] = [];

      try {
        for (const file of files) {
          const uploadUrl = await createUploadUrl({});
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });
          const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response is always { storageId: Id<'_storage'> }
          fileIds.push(storageId);
          fileNames.push(file.name);
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setState(prev => ({ ...prev, step: 'error', error: message }));
        return;
      }

      setState(prev => ({
        ...prev,
        uploadedFileIds: fileIds,
      }));

      try {
        const result = await extractPolicyData({ storageIds: fileIds });
        if (result.success && result.data) {
          setState(prev => ({
            ...prev,
            extractedData: result.data as IExtractedPolicyData, // AS_JUSTIFIED: Convex action return matches IExtractedPolicyData shape
            step: 'review',
            error: null,
          }));
        } else {
          setState(prev => ({
            ...prev,
            step: 'error',
            error: result.error ?? 'Extraction failed',
          }));
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unknown error during extraction';
        setState(prev => ({ ...prev, step: 'error', error: message }));
      }
    },
    [createUploadUrl, extractPolicyData]
  );

  const savePolicy = useCallback(
    async (
      insuranceTypeId: Id<'orgInsuranceTypes'>,
      policyNumber: string,
      customerName: string,
      fieldValues: IPolicyFieldValue[],
      status: 'draft' | 'active' | 'expired' | 'cancelled'
    ) => {
      if (!orgId) return null;

      setState(prev => ({ ...prev, step: 'saving' }));

      try {
        const leadId = await matchOrCreateCustomer({
          orgId,
          name: customerName,
          email: state.extractedData?.policyholder.email,
          phoneNumber: state.extractedData?.policyholder.phone,
        });

        const policyId = await createPolicy({
          orgId,
          insuranceTypeId,
          policyNumber,
          customerName,
          fieldValues,
          status,
          leadId,
          invoiceStatus: 'unknown',
          isImported: true,
          sourceFileIds: state.uploadedFileIds,
          policyDocuments: state.uploadedFileIds.map((storageId, i) => ({
            storageId,
            fileName: state.uploadedFileNames[i] ?? `Document ${i + 1}`,
          })),
          extractedMetadata: state.extractedData ? JSON.stringify(state.extractedData) : undefined,
        });

        // Create driver records from extracted data
        const extractedDrivers = state.extractedData?.drivers ?? [];
        if (extractedDrivers.length > 0) {
          let mainDriverId: string | null = null;

          for (let i = 0; i < extractedDrivers.length; i++) {
            const driver = extractedDrivers[i];
            // First driver (main driver = policyholder) gets the policyholder's ID number
            const isMainDriverEntry = i === 0;
            const driverId = await createDriver({
              policyId,
              fullName: driver.name,
              birthday: '',
              nationality: '',
              idNumber: isMainDriverEntry ? state.extractedData?.policyholder.idNumber : undefined,
              licenseIssueCountry: '',
              licenseIssueDate: '',
            });

            // First driver is the main driver (policyholder)
            if (!mainDriverId) {
              mainDriverId = driverId;
            }
          }

          // Set main driver on the policy
          if (mainDriverId) {
            await updatePolicy({
              policyId,
              mainDriverId: mainDriverId as Id<'policyDrivers'>, // AS_JUSTIFIED: driverId from createDriver is always Id<'policyDrivers'>
            });
          }
        }

        return policyId;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Save failed';
        setState(prev => ({ ...prev, step: 'error', error: message }));
        return null;
      }
    },
    [
      orgId,
      createPolicy,
      updatePolicy,
      createDriver,
      matchOrCreateCustomer,
      state.extractedData,
      state.uploadedFileIds,
      state.uploadedFileNames,
    ]
  );

  return {
    ...state,
    uploadFiles,
    savePolicy,
    reset,
  };
}
